import { normaliseUnit, parseReportMeasurementCell } from './reportMeasurementLabels';

/** The fields of a saved report this history reads. */
export interface HistoryReport {
  _id?: string;
  studyInstanceUID?: string | null;
  studyDate?: string | null;
  createdOn?: string | null;
  status?: string | null;
  templateHTML?: unknown;
}

export interface MeasurementHistoryPoint {
  studyInstanceUID: string;
  date: string | null;
  timestamp: number;
  value: number;
  unit: string;
}

function timestampFor(date: string | null | undefined): number {
  if (!date) return 0;
  if (/^\d{8}$/.test(date)) {
    return Date.UTC(Number(date.slice(0, 4)), Number(date.slice(4, 6)) - 1, Number(date.slice(6, 8)));
  }
  const parsed = Date.parse(date);
  return Number.isFinite(parsed) ? parsed : 0;
}

const decodeEntities = (s: string) =>
  s
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)));

function tipTapText(node: any): string {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return ' ';
  return (node.content || []).map(tipTapText).join(' ');
}

/**
 * Text of every innermost block (paragraph, or a table cell / list item with no paragraph
 * inside) in a saved report body — the same element the hover resolves in the page, so a
 * cell holding several lines is read line by line. Bodies are stored as TipTap JSON
 * (object or JSON string) or as an HTML string, depending on the template.
 */
export function reportCellTexts(templateHTML: unknown): string[] {
  if (!templateHTML) return [];
  if (typeof templateHTML === 'string') {
    const str = templateHTML.trim();
    if (str.startsWith('{')) {
      try {
        return reportCellTexts(JSON.parse(str));
      } catch {
        /* HTML string */
      }
    }
    const cells: string[] = [];
    const text = (inner: string) => decodeEntities(inner.replace(/<[^>]+>/g, ' '));
    str.replace(/<(li|p)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi, (_, __, inner) => {
      cells.push(text(inner));
      return '';
    });
    str.replace(/<(td|th)\b[^>]*>([\s\S]*?)<\/\1\s*>/gi, (_, __, inner) => {
      if (!/<\s*(p|li)\b/i.test(inner)) cells.push(text(inner));
      return '';
    });
    return cells;
  }
  const cells: string[] = [];
  const walk = (node: any) => {
    if (!node) return;
    const children: any[] = node.content || [];
    const isBlock = ['tableCell', 'tableHeader', 'listItem', 'paragraph'].includes(node.type);
    if (isBlock && !children.some(child => ['paragraph', 'listItem'].includes(child?.type))) {
      cells.push(tipTapText(node));
    }
    children.forEach(walk);
  };
  walk(templateHTML);
  return cells;
}

/**
 * One point per study for `measurementId`, read from the values printed in the patient's
 * saved reports. A study with several reports uses the first one given (callers pass
 * newest first). Only values printed in `unit` are kept, so a report printed in mm is
 * never plotted against one printed in cm.
 */
export function buildMeasurementHistoryPoints(
  reports: HistoryReport[],
  measurementId: string,
  unit: string
): MeasurementHistoryPoint[] {
  const wantedUnit = normaliseUnit(unit);
  const seenStudies = new Set<string>();
  const points: MeasurementHistoryPoint[] = [];
  reports.forEach(report => {
    const studyInstanceUID = report.studyInstanceUID || '';
    if (!studyInstanceUID || seenStudies.has(studyInstanceUID)) return;
    seenStudies.add(studyInstanceUID);
    const cell = reportCellTexts(report.templateHTML)
      .map(parseReportMeasurementCell)
      .find(parsed => parsed?.measurementId === measurementId);
    if (!cell || cell.value === null || !Number.isFinite(cell.value)) return;
    if (normaliseUnit(cell.unit) !== wantedUnit) return;
    const date = report.studyDate || report.createdOn || null;
    points.push({ studyInstanceUID, date, timestamp: timestampFor(date), value: cell.value, unit: cell.unit });
  });
  return points.sort((a, b) => a.timestamp - b.timestamp);
}

export function formatMeasurementHistoryDate(date: string | null): string {
  if (!date) return 'Date unavailable';
  if (/^\d{8}$/.test(date)) return `${date.slice(6, 8)}/${date.slice(4, 6)}/${date.slice(0, 4)}`;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('en-AU');
}

export interface MeasurementChange {
  /** Last value minus the first plotted value. */
  sinceFirst: number;
  firstDate: string | null;
  /** Last value minus the one before it. */
  sincePrevious: number;
  previousDate: string | null;
  /** First-to-last change per year; null when the studies are under a month apart. */
  perYear: number | null;
  /** Decimals to print, taken from the most precise printed value. */
  decimals: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const decimalsOf = (value: number) => (String(value).split('.')[1] || '').length;

/** Change across the plotted points (sorted oldest first). Needs two points with dates. */
export function measurementChange(points: MeasurementHistoryPoint[]): MeasurementChange | null {
  if (points.length < 2) return null;
  const first = points[0];
  const previous = points[points.length - 2];
  const last = points[points.length - 1];
  if (!first.timestamp || !last.timestamp) return null;
  const days = (last.timestamp - first.timestamp) / DAY_MS;
  const decimals = Math.max(...points.map(point => decimalsOf(point.value)));
  const round = (value: number) => Number(value.toFixed(decimals + 1));
  return {
    sinceFirst: round(last.value - first.value),
    firstDate: first.date,
    sincePrevious: round(last.value - previous.value),
    previousDate: previous.date,
    perYear: days >= 30 ? round(((last.value - first.value) / days) * 365.25) : null,
    decimals,
  };
}

/** "+0.4", "-1.2", "0" — sign always shown on a change. */
export function formatMeasurementChange(value: number, decimals: number): string {
  const text = Math.abs(value).toFixed(decimals);
  if (Number(text) === 0) return (0).toFixed(decimals);
  return `${value > 0 ? '+' : '−'}${text}`;
}
